if (!customElements.get('product-form')) {
  customElements.define(
    'product-form',
    class ProductForm extends HTMLElement {
      constructor() {
        super();

        this.form = this.querySelector('form');
        this.form && this.form.addEventListener('submit', this.onSubmitHandler.bind(this));
        this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        this.submitButton = this.querySelector('[type="submit"]');
        this.hideErrors = this.dataset.hideErrors === 'true';
      }

      onSubmitHandler(evt) {
        evt.preventDefault();
        if (this.submitButton.getAttribute('aria-disabled') === 'true') return;

        this.handleErrorMessage();
        this.enforceMinimumQuantity();

        this.submitButton.setAttribute('aria-disabled', true);
        this.submitButton.classList.add('loading');
        this.querySelector('.loading-overlay__spinner').classList.remove('hidden');

        const config = fetchConfig('javascript');
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        delete config.headers['Content-Type'];

        const formData = new FormData(this.form);
        const upsellData = this.getUpsellTrackingData(formData);
        this.removeUpsellTrackingFields(formData);
        if (this.cart) {
          formData.append(
            'sections',
            this.cart.getSectionsToRender().map((section) => section.id)
          );
          formData.append('sections_url', window.location.pathname);
          this.cart.setActiveElement(document.activeElement);
        }
        config.body = formData;

        fetch(`${routes.cart_add_url}`, config)
          .then((response) => response.json())
          .then((response) => {
            if (response.status) {
              publish(PUB_SUB_EVENTS.cartError, {
                source: 'product-form',
                productVariantId: formData.get('id'),
                errors: response.errors || response.description,
                message: response.message,
              });
              this.handleErrorMessage(response.description);

              const soldOutMessage = this.submitButton.querySelector('.sold-out-message');
              if (!soldOutMessage) return;
              this.submitButton.setAttribute('aria-disabled', true);
              this.submitButton.querySelector('span').classList.add('hidden');
              soldOutMessage.classList.remove('hidden');
              this.error = true;
              return;
            }

            // Render live carts immediately; finish tracking before a fallback redirect.
            const upsellTracking = this.trackCartUpsell(upsellData);

            if (!this.cart) {
              if (upsellTracking) {
                return upsellTracking.finally(() => {
                  window.location = window.routes.cart_url;
                });
              }
              window.location = window.routes.cart_url;
              return;
            }

            const renderCart = () => {
              if (!this.error)
                publish(PUB_SUB_EVENTS.cartUpdate, {
                  source: 'product-form',
                  productVariantId: formData.get('id'),
                  cartData: response,
                });
              this.error = false;
              this.cart.renderContents(response);
            };

            renderCart();
          })
          .catch((e) => {
            console.error(e);
          })
          .finally(() => {
            this.submitButton.classList.remove('loading');
            if (this.cart && this.cart.classList.contains('is-empty')) this.cart.classList.remove('is-empty');
            if (!this.error) this.submitButton.removeAttribute('aria-disabled');
            this.querySelector('.loading-overlay__spinner').classList.add('hidden');
          });
      }

      enforceMinimumQuantity() {
        const quantityInput = this.getQuantityInput();
        if (!quantityInput) return;

        const minimumQuantity = this.getMinimumQuantity(quantityInput);
        const quantityStep = this.getQuantityStep(quantityInput, minimumQuantity);
        const maximumQuantity = this.getMaximumAllowedQuantity(quantityInput, minimumQuantity, quantityStep);
        const quantityValue = parseInt(quantityInput.value);
        if (Number.isNaN(minimumQuantity) || minimumQuantity < 1) return;

        let nextQuantity = Number.isNaN(quantityValue) ? minimumQuantity : quantityValue;
        if (nextQuantity <= minimumQuantity) {
          nextQuantity = minimumQuantity;
        } else {
          nextQuantity = minimumQuantity + Math.ceil((nextQuantity - minimumQuantity) / quantityStep) * quantityStep;
        }

        if (maximumQuantity !== null) {
          nextQuantity = Math.min(nextQuantity, maximumQuantity);
        }

        if (quantityValue !== nextQuantity) {
          quantityInput.value = nextQuantity;
          if (quantityInput.dataset.minimumQuantityMessage) {
            this.handleErrorMessage(quantityInput.dataset.minimumQuantityMessage);
          }
        }
      }

      getQuantityInput() {
        const formQuantityInput = this.form.querySelector('input[name="quantity"][data-minimum-quantity]');
        if (formQuantityInput) return formQuantityInput;

        return Array.from(document.querySelectorAll('input[name="quantity"][data-minimum-quantity]')).find(
          (input) => input.form === this.form
        );
      }

      getMinimumQuantity(quantityInput) {
        const dataMinimum = parseInt(quantityInput.dataset.minimumQuantity);
        const inputMinimum = parseInt(quantityInput.min);

        if (!Number.isNaN(dataMinimum) && dataMinimum > 0) return dataMinimum;
        if (!Number.isNaN(inputMinimum) && inputMinimum > 0) return inputMinimum;

        return 1;
      }

      getQuantityStep(quantityInput, minimumQuantity) {
        const dataStep = parseInt(quantityInput.dataset.quantityStep);
        const inputStep = parseInt(quantityInput.step);

        if (!Number.isNaN(dataStep) && dataStep > 0) return dataStep;
        if (!Number.isNaN(inputStep) && inputStep > 0) return inputStep;

        return minimumQuantity;
      }

      getMaximumQuantity(quantityInput, minimumQuantity, quantityStep) {
        const maximum = parseInt(quantityInput.max);
        return !Number.isNaN(maximum) && maximum > 0 ? maximum : null;
      }

      getMaximumAllowedQuantity(quantityInput, minimumQuantity, quantityStep) {
        const maximumQuantity = this.getMaximumQuantity(quantityInput, minimumQuantity, quantityStep);

        if (maximumQuantity === null) return null;
        if (maximumQuantity <= minimumQuantity) return minimumQuantity;

        return minimumQuantity + Math.floor((maximumQuantity - minimumQuantity) / quantityStep) * quantityStep;
      }

      getUpsellTrackingData(formData) {
        const params = new URLSearchParams(window.location.search);
        const upsellFlag = formData.get('cart_upsell') || formData.get('properties[_cart_upsell]') || params.get('cart_upsell');
        if (!upsellFlag) return null;

        const pageTitle = document.querySelector('h1') ? document.querySelector('h1').textContent.trim() : document.title;

        return {
          source:
            formData.get('cart_upsell_source') ||
            formData.get('properties[_cart_upsell_source]') ||
            params.get('upsell_source') ||
            'product-page',
          tag:
            formData.get('cart_upsell_tag') ||
            formData.get('properties[_cart_upsell_tag]') ||
            params.get('upsell_tag') ||
            '',
          product:
            formData.get('cart_upsell_product') ||
            formData.get('properties[_cart_upsell_product]') ||
            pageTitle,
          missing:
            formData.get('cart_upsell_missing') ||
            formData.get('properties[_cart_upsell_missing]') ||
            params.get('upsell_missing') ||
            '',
        };
      }

      removeUpsellTrackingFields(formData) {
        [
          'cart_upsell',
          'cart_upsell_source',
          'cart_upsell_tag',
          'cart_upsell_product',
          'cart_upsell_missing',
          'properties[_cart_upsell]',
          'properties[_cart_upsell_source]',
          'properties[_cart_upsell_tag]',
          'properties[_cart_upsell_product]',
          'properties[_cart_upsell_missing]',
        ].forEach((field) => formData.delete(field));
      }

      getCartNoteFromPage() {
        const noteField = document.querySelector('[name="note"]:focus') || document.querySelector('[name="note"]');
        return noteField ? noteField.value || '' : null;
      }

      getCurrentCartNote() {
        const pageNote = this.getCartNoteFromPage();
        if (pageNote !== null) return Promise.resolve(pageNote);

        return fetch(`${routes.cart_url}.js`, { credentials: 'same-origin' })
          .then((response) => {
            if (!response.ok) throw new Error(`Cart note fetch failed (${response.status})`);
            return response.json();
          })
          .then((cart) => cart.note || '');
      }

      getUpsellNote(existingNote) {
        const note = (existingNote || '').trim();
        const hasMarker = note.split(/\r?\n/).some((line) => line.trim().toLowerCase() === 'upsell');
        if (hasMarker) return note;
        return note ? `${note}\nUpsell` : 'Upsell';
      }

      trackCartUpsell(upsellData) {
        if (!upsellData) return null;

        return this.getCurrentCartNote()
          .catch((error) => {
            console.warn(error);
            return null;
          })
          .then((note) => {
            const cartUpdate = {
              attributes: {
                'Upsell dodatak': 'Da',
                'Upsell izvor': upsellData.source || 'cart',
                'Upsell tag korpe': upsellData.tag || '',
                'Upsell proizvod': upsellData.product || '',
                'Upsell razlika': upsellData.missing || '',
              },
            };
            if (note !== null) cartUpdate.note = this.getUpsellNote(note);

            return fetch(`${routes.cart_update_url}`, {
              ...fetchConfig('javascript'),
              body: JSON.stringify(cartUpdate),
              keepalive: true,
            });
          })
          .then((response) => {
            if (!response.ok) throw new Error(`Upsell tracking failed (${response.status})`);
          })
          .catch((e) => {
            console.warn(e);
          });
      }

      handleErrorMessage(errorMessage = false) {
        if (this.hideErrors) return;

        this.errorMessageWrapper =
          this.errorMessageWrapper || this.querySelector('.product-form__error-message-wrapper');
        if (!this.errorMessageWrapper) return;
        this.errorMessage = this.errorMessage || this.errorMessageWrapper.querySelector('.product-form__error-message');

        this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);

        if (errorMessage) {
          this.errorMessage.textContent = errorMessage;
        }
      }
    }
  );
}

if (!window.cartFreeDeliveryUpsellDismissBound) {
  window.cartFreeDeliveryUpsellDismissBound = true;

  const cartUpsellDismissedClass = 'cart-free-delivery-upsell-dismissed';
  const cartUpsellDismissedKey = 'cartFreeDeliveryUpsellDismissed';

  try {
    if (sessionStorage.getItem(cartUpsellDismissedKey) === 'true') {
      document.documentElement.classList.add(cartUpsellDismissedClass);
    }
  } catch (e) {
    document.documentElement.classList.remove(cartUpsellDismissedClass);
  }

  document.addEventListener('click', (event) => {
    if (!event.target.closest) return;
    const dismissButton = event.target.closest('[data-cart-free-delivery-upsell-dismiss]');
    if (!dismissButton) return;

    event.preventDefault();
    const upsell = dismissButton.closest('.cart-free-delivery-upsell');
    if (upsell) upsell.setAttribute('hidden', 'hidden');
    document.documentElement.classList.add(cartUpsellDismissedClass);

    try {
      sessionStorage.setItem(cartUpsellDismissedKey, 'true');
    } catch (e) {
      return;
    }
  });
}

class stickyCartModal extends HTMLElement {
  constructor() {
    super();
    window.addEventListener('scroll', this.update.bind(this));
    this.update();
    this.querySelector('.sticky-cart-button').addEventListener('click', this.onClickHandler.bind(this));
    this.querySelector('.sticky-cart-button').addEventListener('keydown', this.onClickKeydownHandler.bind(this));
  }
  update(){
    var button = document.getElementById(`product-form-${this.dataset.section}`);
    var sect = this.closest('.shopify-section')||this.closest('.popup-modal__content__data');
    var addButton = sect.querySelector('select-option-js')?button.querySelector('.select-options-button'):button.querySelector('[name="add"]');
    const rangeToShowModal = this.getTop(addButton) + addButton.clientHeight;

    if(rangeToShowModal <= 0) return false;
    if(window.scrollY >= rangeToShowModal && !this.classList.contains('show-modal')){
      this.classList.add('show-modal');
      document.querySelector('body').classList.add('modal__sticky-cart');
      document.querySelector('.footer__content').classList.add('sticky-cart-modal_bottom_padding');
    }
    if(window.scrollY < rangeToShowModal && this.classList.contains('show-modal')){
      this.classList.remove('show-modal');
      document.querySelector('body').classList.remove('modal__sticky-cart');
    }
  }
  onClickKeydownHandler(){
    if(event.code.toUpperCase() !== 'ENTER') return false;
    this.onClickHandler();
  }
  onClickHandler(){
    
    var item = document.querySelector(`.product__column__content [id*="variant-selects-"]`),
        btn_item = document.getElementById("product-container"),
        _y = (item ? this.getTop(item) : this.getTop(btn_item)) - 20;
    this.scrollTo(_y,700);
  }
  getTop(el) {
    return el.offsetTop + (el.offsetParent && this.getTop(el.offsetParent));
  }
  scrollTo(to, duration) {
    const element = document.scrollingElement || document.documentElement,
          start = element.scrollTop,
          change = to - start,
    animateScroll = function() {
      element.scrollTop = to;
    };
    animateScroll();
  }
}
customElements.define('sticky-cart-modal', stickyCartModal);
